import { useState, useEffect, useRef } from "react";
import { api } from "./context";

export const TriviaChallengeCard = ({ msg, myAlias, dark, flash, activeRoom, onGameStart }) => {
  const meta = msg.meta || {};
  const gameId = meta.game_id;
  const [accepting, setAccepting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [gameState, setGameState] = useState(null);
  const text = dark ? "#ffffff" : "#1a1c2e";
  const faint = dark ? "#ffffff30" : "#9ca0b8";

  useEffect(() => {
    if (gameId) api(`/trivia/${gameId}/state`).then(r => { if (r.ok) setGameState(r.data.game); });
  }, [gameId]);

  const handleAccept = async () => {
    setAccepting(true);
    const r = await api(`/trivia/${gameId}/accept`, { method: "POST", body: JSON.stringify({ alias: myAlias }) });
    setAccepting(false);
    if (r.ok) { flash("Game started! 🎮"); onGameStart(r.data.game); }
    else flash(r.message || "Cannot accept", "error");
  };

  const handleCancel = async () => {
    setCancelling(true);
    const r = await api(`/trivia/${gameId}/cancel`, { method: "POST" });
    setCancelling(false);
    if (r.ok) flash("Challenge cancelled, GT refunded");
    else flash(r.message || "Cannot cancel", "error");
  };

  const status = gameState?.status || "pending";
  const isExpired = status === "expired" || status === "cancelled";
  const isActive = status === "active" || status === "completed";
  const isMyChallenge = gameState?.is_challenger;
  const canAccept = !isMyChallenge && status === "pending";

  return (
    <div style={{ background: dark ? "#12122a" : "#f0edff", border: `1.5px solid ${isActive ? "#10b981" : isExpired ? "#ef444440" : "#6c5ce7"}`, borderRadius: 16, padding: 12, width: "100%", margin: "4px 0", opacity: isExpired ? 0.6 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 16 }}>🎮</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#6c5ce7" }}>Trivia Duo</span>
        </div>
        <span style={{ fontSize: 10, color: isExpired ? "#ef4444" : isActive ? "#10b981" : "#f59e0b", fontWeight: 700, background: isExpired ? "#ef444415" : isActive ? "#10b98115" : "#f59e0b15", padding: "2px 8px", borderRadius: 10 }}>
          {isExpired ? "Expired" : isActive ? (status === "completed" ? "Done" : "In Progress") : "⏳ Waiting"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div style={{ fontSize: 18 }}>🎭</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: text }}>{meta.challenger_alias}</div>
          <div style={{ fontSize: 9, color: faint }}>Challenger</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, color: faint }}>Bet</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#f59e0b" }}>{meta.bet_amount} GT</div>
          <div style={{ fontSize: 9, color: faint }}>5 rounds</div>
        </div>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div style={{ fontSize: 18 }}>{gameState?.opponent_alias ? "🎭" : "❓"}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: text }}>{gameState?.opponent_alias || "Anyone?"}</div>
          <div style={{ fontSize: 9, color: faint }}>Opponent</div>
        </div>
      </div>
      {!isExpired && !isActive && (
        <div style={{ display: "flex", gap: 8 }}>
          {canAccept && <button onClick={handleAccept} disabled={accepting} style={{ flex: 2, padding: "10px 0", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: accepting ? 0.7 : 1 }}>{accepting ? "Joining..." : `⚔️ Accept — ${meta.bet_amount} GT`}</button>}
          {isMyChallenge && <button onClick={handleCancel} disabled={cancelling} style={{ flex: 1, padding: "10px 0", borderRadius: 12, border: "1px solid #ef444440", background: "#ef444415", color: "#ef4444", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{cancelling ? "..." : "Cancel"}</button>}
          {!canAccept && !isMyChallenge && <div style={{ flex: 1, textAlign: "center", fontSize: 11, color: faint, padding: "10px 0" }}>Waiting...</div>}
        </div>
      )}
      {isActive && status !== "completed" && <div style={{ textAlign: "center", fontSize: 11, color: "#10b981", fontWeight: 700 }}>🎮 Game in progress!</div>}
      {status === "completed" && gameState && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>🏆 {gameState.winner_id ? (gameState.challenger_score > gameState.opponent_score ? `${gameState.challenger_alias} wins!` : `${gameState.opponent_alias} wins!`) : "Draw!"}</div>
          <div style={{ fontSize: 10, color: faint }}>{gameState.challenger_score} — {gameState.opponent_score}</div>
        </div>
      )}
    </div>
  );
};

export const TriviaGameBar = ({ game, dark, onExpand }) => {
  if (!game || game.status === "completed" || game.status === "cancelled") return null;
  const myScore = game.is_challenger ? game.challenger_score : game.opponent_score;
  const opScore = game.is_challenger ? game.opponent_score : game.challenger_score;
  const myAlias = game.is_challenger ? game.challenger_alias : game.opponent_alias;
  const opAlias = game.is_challenger ? game.opponent_alias : game.challenger_alias;
  return (
    <div onClick={onExpand} style={{ background: dark ? "#0d0d1a" : "#f0edff", borderBottom: "2px solid #6c5ce7", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>🎮</span>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#6c5ce7", letterSpacing: 1 }}>TRIVIA DUO — LIVE</div>
          <div style={{ fontSize: 9, color: dark ? "#ffffff50" : "#6b7094" }}>Round {game.current_round}/{game.total_rounds} · {game.bet_amount > 0 ? `${game.bet_amount} GT` : "Free"}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#06b6d4" }}>{myAlias?.split(" ")[0]}</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#06b6d4" }}>{myScore}</div>
        </div>
        <div style={{ fontSize: 11, color: dark ? "#ffffff30" : "#9ca0b8", fontWeight: 700 }}>vs</div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#a855f7" }}>{opAlias?.split(" ")[0]}</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#a855f7" }}>{opScore}</div>
        </div>
        <div style={{ fontSize: 10, color: "#6c5ce7", fontWeight: 700, marginLeft: 4 }}>Tap ›</div>
      </div>
    </div>
  );
};

export const TriviaGameModal = ({ game: initialGame, dark, flash, myAlias, onClose, onGameEnd }) => {
  const [game, setGame] = useState(initialGame);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(20);
  const [showResult, setShowResult] = useState(false);
  const [nextRoundCountdown, setNextRoundCountdown] = useState(null);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const roundRef = useRef(initialGame.current_round);
  const submittedRef = useRef(false);
  const gameEndingRef = useRef(false);

  const myScore = game.is_challenger ? game.challenger_score : game.opponent_score;
  const opScore = game.is_challenger ? game.opponent_score : game.challenger_score;
  const myAliasFull = game.is_challenger ? game.challenger_alias : game.opponent_alias;
  const opAliasFull = game.is_challenger ? game.opponent_alias : game.challenger_alias;

  const catLabel = (cat) => ({ general: "🌐 GENERAL KNOWLEDGE", spicy: "🔥 SPICY", adult_toys: "🎭 ADULT TOYS", sexual_health: "❤️ SEXUAL HEALTH" }[cat] || "🌐 GENERAL KNOWLEDGE");

  const loadState = async () => {
    const r = await api(`/trivia/${game.id}/state`);
    if (!r.ok) return;
    const ng = r.data.game;
    if (ng.current_round !== roundRef.current && !showResult) {
      roundRef.current = ng.current_round;
      submittedRef.current = false;
      setGame(ng); setSelectedAnswer(null); setSubmitted(false); setShowResult(false); setTimeLeft(20);
      return;
    }
    setGame(ng);
    if (ng.round_result?.challenger_answer && ng.round_result?.opponent_answer && !showResult) {
      clearInterval(timerRef.current);
      setShowResult(true);

      // If last round — go to game over immediately
      if (ng.status === "completed") {
        if (gameEndingRef.current) return;
        gameEndingRef.current = true;
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        clearInterval(countdownRef.current);
        setNextRoundCountdown(null);
        onGameEnd(ng);
        return;
      }

      // Otherwise countdown to next round
      let c = 3; setNextRoundCountdown(c);
      countdownRef.current = setInterval(() => {
        c--; setNextRoundCountdown(c);
        if (c <= 0) {
          clearInterval(countdownRef.current);
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          roundRef.current = ng.current_round + 1;
          submittedRef.current = false;
          setGame(ng);
          setShowResult(false);
          setSelectedAnswer(null);
          setSubmitted(false);
          setTimeLeft(20);
          pollRef.current = setInterval(loadState, 800);
        }
      }, 1000);
    }
    if (ng.status === "completed" && !gameEndingRef.current) {
      gameEndingRef.current = true;
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
      onGameEnd(ng);
    }
  };

  useEffect(() => {
    pollRef.current = setInterval(loadState, 800);
    return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); clearInterval(countdownRef.current); };
  }, [game.id]);

  useEffect(() => {
    if (submitted || showResult) return;
    clearInterval(timerRef.current);
    setTimeLeft(20);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); if (!submittedRef.current) handleSubmit("a"); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [game.current_round, showResult]);

  const handleSubmit = async (answer) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true); setSelectedAnswer(answer);
    clearInterval(timerRef.current);
    await api(`/trivia/${game.id}/answer`, { method: "POST", body: JSON.stringify({ answer, round: game.current_round }) });
  };

  const q = game.question;
  const result = game.round_result;
  const myAnswer = result ? (game.is_challenger ? result.challenger_answer : result.opponent_answer) : null;
  const opAnswer = result ? (game.is_challenger ? result.opponent_answer : result.challenger_answer) : null;
  const correctAnswer = result?.correct_answer;
  const iWonRound = result?.round_winner === (game.is_challenger ? "challenger" : "opponent");
  const isDrawRound = result?.round_winner === "draw";
  const timerPct = (timeLeft / 20) * 100;
  const timerColor = timeLeft <= 5 ? "#ef4444" : timeLeft <= 10 ? "#f59e0b" : "#6c5ce7";
  const R = 28; const circ = 2 * Math.PI * R;

  const optBg = (key) => {
    if (showResult) { if (key === correctAnswer) return "rgba(16,185,129,0.15)"; if (key === myAnswer) return "rgba(239,68,68,0.15)"; return "rgba(255,255,255,0.02)"; }
    if (selectedAnswer === key) return "rgba(6,182,212,0.12)";
    return "rgba(255,255,255,0.02)";
  };
  const optBorder = (key) => {
    if (showResult) { if (key === correctAnswer) return "#10b981"; if (key === myAnswer) return "#ef4444"; return "rgba(255,255,255,0.08)"; }
    if (selectedAnswer === key) return "#06b6d4";
    return "rgba(255,255,255,0.08)";
  };
  const optLabelBg = (key) => {
    if (showResult) { if (key === correctAnswer) return "#10b981"; if (key === myAnswer) return "#ef4444"; return "rgba(255,255,255,0.08)"; }
    if (selectedAnswer === key) return "#2563eb";
    return "transparent";
  };

  if (gameEndingRef.current) return null;

  const PlayerCard = ({ alias, score, isMe, answered }) => {
    const color = isMe ? "#06b6d4" : "#a855f7";
    const borderColor = isMe ? "rgba(6,182,212,0.3)" : "rgba(168,85,247,0.3)";
    const gradBg = isMe ? "linear-gradient(to bottom, rgba(6,182,212,0.12), transparent)" : "linear-gradient(to bottom, rgba(168,85,247,0.12), transparent)";
    return (
      <div style={{ flex: "0 0 22%", background: gradBg, border: `1px solid ${borderColor}`, borderRadius: 20, padding: "14px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, boxShadow: isMe ? "0 0 30px rgba(6,182,212,0.1)" : "0 0 30px rgba(168,85,247,0.1)" }}>
        <div style={{ width: 44, height: 44, borderRadius: 22, background: `linear-gradient(135deg, ${color}, ${isMe ? "#3b82f6" : "#e91e8c"})`, border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, color: "#fff", position: "relative" }}>
          {alias?.[0]?.toUpperCase() || "?"}
          <div style={{ position: "absolute", bottom: -2, right: -2, width: 10, height: 10, borderRadius: 5, background: "#4ade80", border: "2px solid #04050D" }} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", textAlign: "center", maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{alias}</div>
        {showResult ? (
          <div style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: myAnswer === correctAnswer && isMe ? "rgba(16,185,129,0.2)" : !isMe && opAnswer === correctAnswer ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)", color: (isMe ? myAnswer : opAnswer) === correctAnswer ? "#4ade80" : "#ef4444", border: `1px solid ${(isMe ? myAnswer : opAnswer) === correctAnswer ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}` }}>
            {(isMe ? myAnswer : opAnswer) === correctAnswer ? "✓ Correct!" : "✗ Wrong"}
          </div>
        ) : (
          <div style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.25)" }}>✔ Ready</div>
        )}
        <div style={{ width: "100%", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8, marginTop: 2 }}>
          <div style={{ fontSize: 8, color: color, textTransform: "uppercase", letterSpacing: 1, textAlign: "center", marginBottom: 4 }}>{isMe ? "Your Bet" : "Their Bet"}</div>
          <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, padding: "6px 8px", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, border: "1px solid rgba(255,255,255,0.06)" }}>
            <span style={{ fontSize: 12 }}>🪙</span>
            <span style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>{game.bet_amount}</span>
          </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: 8, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1 }}>{isMe ? "YOU" : "OPP"}</div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#04050D", zIndex: 9000, display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif", overflow: "hidden" }}>
      <div style={{ position: "fixed", inset: 0, background: "radial-gradient(circle at top, rgba(88,28,255,0.18), transparent 40%)", pointerEvents: "none" }} />

      {/* Header */}
      <div style={{ position: "relative", zIndex: 1, background: "rgba(255,255,255,0.02)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <button onClick={async () => {
          if (window.confirm("Leave game? You will forfeit and lose your bet!")) {
            await api(`/trivia/${game.id}/cancel`, { method: "POST" });
            onClose();
          }
        }} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>← Leave</button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🎮</div>
          <span style={{ fontSize: 13, fontWeight: 900, color: "#fff", letterSpacing: 2 }}>TRIVIA DUO</span>
        </div>
        <div style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: "4px 10px", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 11 }}>🪙</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b" }}>{game.bet_amount > 0 ? `${game.bet_amount} GT` : "Free"}</span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", position: "relative", zIndex: 1 }}>

        {/* Round badge */}
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "4px 16px", fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: 2, textTransform: "uppercase" }}>
            Round {game.current_round} of {game.total_rounds}
          </div>
        </div>

        {/* 3-column: player | center | player */}
        <div style={{ display: "flex", gap: 8, padding: "10px 12px 0", alignItems: "flex-start" }}>
          <PlayerCard alias={myAliasFull} score={myScore} isMe={true} answered={submitted} />

          {/* Center column */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            {/* Circular timer */}
            {!showResult && (
              <div style={{ position: "relative", width: 64, height: 64 }}>
                <svg width="64" height="64" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="32" cy="32" r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
                  <circle cx="32" cy="32" r={R} fill="none" stroke={timerColor} strokeWidth="5"
                    strokeDasharray={circ} strokeDashoffset={circ * (1 - timerPct / 100)}
                    strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s" }} />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 16, fontWeight: 900, color: timerColor, lineHeight: 1 }}>{timeLeft}</span>
                  <span style={{ fontSize: 7, color: "rgba(255,255,255,0.3)", marginTop: 1 }}>SEC</span>
                </div>
              </div>
            )}

            {/* Round result banner */}
            {showResult && result && (
              <div style={{ width: "100%", background: iWonRound ? "rgba(16,185,129,0.15)" : isDrawRound ? "rgba(245,158,11,0.15)" : "rgba(239,68,68,0.15)", border: `1px solid ${iWonRound ? "#10b981" : isDrawRound ? "#f59e0b" : "#ef4444"}`, borderRadius: 14, padding: "10px", textAlign: "center" }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{iWonRound ? "🏆" : isDrawRound ? "🤝" : "💀"}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: iWonRound ? "#10b981" : isDrawRound ? "#f59e0b" : "#ef4444", marginBottom: 2 }}>{iWonRound ? "You Win!" : isDrawRound ? "Draw!" : "Opponent Wins!"}</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>Correct: <strong style={{ color: "#fff" }}>{q?.[`option_${correctAnswer}`]}</strong></div>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <div style={{ flex: 1, background: myAnswer === correctAnswer ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)", borderRadius: 8, padding: "5px 6px", border: `1px solid ${myAnswer === correctAnswer ? "#10b981" : "#ef4444"}` }}>
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.4)", marginBottom: 1 }}>You</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: myAnswer === correctAnswer ? "#10b981" : "#ef4444" }}>{myAnswer?.toUpperCase()}: {q?.[`option_${myAnswer}`]?.slice(0, 8) || "—"}</div>
                  </div>
                  <div style={{ flex: 1, background: opAnswer === correctAnswer ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)", borderRadius: 8, padding: "5px 6px", border: `1px solid ${opAnswer === correctAnswer ? "#10b981" : "#ef4444"}` }}>
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.4)", marginBottom: 1 }}>Them</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: opAnswer === correctAnswer ? "#10b981" : "#ef4444" }}>{opAnswer?.toUpperCase()}: {q?.[`option_${opAnswer}`]?.slice(0, 8) || "—"}</div>
                  </div>
                </div>
                <div style={{ background: iWonRound ? "rgba(16,185,129,0.2)" : isDrawRound ? "rgba(245,158,11,0.2)" : "rgba(239,68,68,0.2)", borderRadius: 10, padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, border: `1px solid ${iWonRound ? "rgba(16,185,129,0.3)" : isDrawRound ? "rgba(245,158,11,0.3)" : "rgba(239,68,68,0.3)"}` }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.4)" }}>You Won</div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: iWonRound ? "#10b981" : "#ef4444" }}>{iWonRound ? `+${game.bet_amount}` : `-${game.bet_amount}`}</div>
                  </div>
                  <div style={{ width: 1, height: 24, background: "rgba(255,255,255,0.1)" }} />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.4)" }}>Opp Lost</div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: !iWonRound && !isDrawRound ? "#10b981" : "#ef4444" }}>{!iWonRound && !isDrawRound ? `+${game.bet_amount}` : `-${game.bet_amount}`}</div>
                  </div>
                </div>
                {game.current_round < game.total_rounds && (
                  <div style={{ background: "linear-gradient(135deg,#3b82f6,#7c3aed)", borderRadius: 10, padding: "8px", fontSize: 11, fontWeight: 800, color: "#fff", cursor: "pointer" }}>
                    NEXT QUESTION ({nextRoundCountdown}s)
                  </div>
                )}
                {game.current_round >= game.total_rounds && (
                  <div style={{ background: "linear-gradient(135deg,#10b981,#059669)", borderRadius: 10, padding: "8px", fontSize: 11, fontWeight: 800, color: "#fff" }}>
                    🏁 Game Over — Loading results...
                  </div>
                )}
              </div>
            )}

            {/* Question */}
            {q && !showResult && (
              <div style={{ width: "100%" }}>
                <div style={{ textAlign: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#d8b4fe", background: "rgba(168,85,247,0.15)", padding: "3px 10px", borderRadius: 20, border: "1px solid rgba(168,85,247,0.25)", letterSpacing: 1 }}>{catLabel(q.category)}</span>
                </div>
                <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "14px 12px", marginBottom: 8, boxShadow: "0 0 40px rgba(139,92,246,0.12)" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", textAlign: "center", lineHeight: 1.5 }}>{q.question}</div>
                </div>
                {["a","b","c","d"].map(key => (
                  <button key={key} onClick={() => !submitted && handleSubmit(key)}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 12, border: `1.5px solid ${optBorder(key)}`, background: optBg(key), marginBottom: 7, cursor: submitted ? "default" : "pointer", transition: "all 0.2s", boxShadow: selectedAnswer === key && !showResult ? "0 0 20px rgba(6,182,212,0.2)" : "none" }}>
                    <div style={{ width: 28, height: 28, borderRadius: 14, background: optLabelBg(key), border: selectedAnswer !== key && !showResult ? "1px solid rgba(255,255,255,0.15)" : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
                      {key.toUpperCase()}
                    </div>
                    <span style={{ fontSize: 12, color: "#fff", flex: 1, textAlign: "left" }}>{q[`option_${key}`]}</span>
                    {showResult && key === correctAnswer && <span>✅</span>}
                    {showResult && key === myAnswer && key !== correctAnswer && <span>❌</span>}
                    {submitted && !showResult && selectedAnswer === key && <span style={{ fontSize: 9, color: "#f59e0b", fontWeight: 700 }}>Waiting...</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Question (show during result) */}
            {q && showResult && (
              <div style={{ width: "100%", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", textAlign: "center", lineHeight: 1.4 }}>{q.question}</div>
              </div>
            )}

            {/* Opponent status */}
            {submitted && !showResult && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "6px 14px" }}>
                <div style={{ width: 6, height: 6, borderRadius: 3, background: game.opponent_answered ? "#10b981" : "#f59e0b" }} />
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{game.opponent_answered ? "Opponent answered!" : "Waiting for opponent..."}</span>
              </div>
            )}

            {/* Progress dots */}
            <div style={{ display: "flex", gap: 5, padding: "8px 0 16px" }}>
              {Array.from({ length: game.total_rounds }).map((_, i) => (
                <div key={i} style={{ width: i + 1 === game.current_round ? 18 : 7, height: 7, borderRadius: 4, background: i + 1 < game.current_round ? "#10b981" : i + 1 === game.current_round ? "#6c5ce7" : "rgba(255,255,255,0.1)", transition: "all 0.3s" }} />
              ))}
            </div>
          </div>

          <PlayerCard alias={opAliasFull} score={opScore} isMe={false} answered={game.opponent_answered} />
        </div>
      </div>
    </div>
  );
};

export const TriviaGameOver = ({ game, dark, onClose, onRematch }) => {
  const myScore = game.is_challenger ? game.challenger_score : game.opponent_score;
  const opScore = game.is_challenger ? game.opponent_score : game.challenger_score;
  const myAlias = game.is_challenger ? game.challenger_alias : game.opponent_alias;
  const opAlias = game.is_challenger ? game.opponent_alias : game.challenger_alias;
  const iWon = myScore > opScore;
  const isDraw = myScore === opScore;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#04050D", zIndex: 9000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif", padding: 20 }}>
      <div style={{ position: "fixed", inset: 0, background: "radial-gradient(circle at top, rgba(88,28,255,0.18), transparent 40%)", pointerEvents: "none" }} />
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, textAlign: "center" }}>
        <div style={{ fontSize: 64, marginBottom: 8 }}>{isDraw ? "🤝" : iWon ? "🏆" : "💀"}</div>
        <div style={{ fontSize: 28, fontWeight: 900, color: isDraw ? "#f59e0b" : iWon ? "#10b981" : "#ef4444", marginBottom: 4 }}>{isDraw ? "It's a Draw!" : iWon ? "You Win!" : "You Lost!"}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 24 }}>{isDraw ? "GT refunded" : iWon ? `+${game.bet_amount * 2} GT earned! 🎉` : `${game.bet_amount} GT lost`}</div>

        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", marginBottom: 16 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 28, background: "linear-gradient(135deg,#06b6d4,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900, color: "#fff", margin: "0 auto 6px", border: "3px solid #06b6d4" }}>{myAlias?.[0] || "?"}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{myAlias}</div>
              <div style={{ fontSize: 40, fontWeight: 900, color: iWon ? "#10b981" : "#ef4444" }}>{myScore}</div>
            </div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.3)", fontWeight: 700 }}>vs</div>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 28, background: "linear-gradient(135deg,#a855f7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900, color: "#fff", margin: "0 auto 6px", border: "3px solid #a855f7" }}>{opAlias?.[0] || "?"}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{opAlias}</div>
              <div style={{ fontSize: 40, fontWeight: 900, color: !iWon && !isDraw ? "#10b981" : "#ef4444" }}>{opScore}</div>
            </div>
          </div>
          <div style={{ background: isDraw ? "rgba(245,158,11,0.1)" : iWon ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${isDraw ? "#f59e0b30" : iWon ? "#10b98130" : "#ef444430"}`, borderRadius: 14, padding: "12px 16px", display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>GT Change</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: isDraw ? "#f59e0b" : iWon ? "#10b981" : "#ef4444" }}>{isDraw ? "±0" : iWon ? `+${game.bet_amount * 2}` : `-${game.bet_amount}`} GT</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>Bet each</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#fff" }}>{game.bet_amount} GT</div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "16px", borderRadius: 16, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
            {game.room_id ? "💬 Chat" : "🎮 Games"}
          </button>
          {!game.room_id && onRematch && (
            <button onClick={onRematch} style={{ flex: 2, padding: "16px", borderRadius: 16, border: "none", background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 30px rgba(245,158,11,0.4)" }}>
              ⚔️ Rematch!
            </button>
          )}
        </div>
      </div>
    </div>
  );
};